import React, { useEffect, useRef, useState } from 'react'
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { FlowchartAiMessage } from '../../../types/flowchartAi'

type FlowAiChatProps = {
  open: boolean
  onOpen: () => void
  onClose: () => void
  messages: FlowchartAiMessage[]
  sending: boolean
  error: string | null
  canSend: boolean
  onSend: (message: string) => Promise<void> | void
  title?: string
}

export function FlowAiChat({
  open,
  onOpen,
  onClose,
  messages,
  sending,
  error,
  canSend,
  onSend,
  title = 'Assistente do fluxograma',
}: FlowAiChatProps) {
  const [input, setInput] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, sending, open])

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => inputRef.current?.focus(), 50)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  const handleSubmit = async () => {
    const text = input.trim()
    if (!text || sending || !canSend) return
    setInput('')
    await onSend(text)
  }

  if (!open) {
    return (
      <button
        type="button"
        className="flow-ai-fab"
        onClick={onOpen}
        title="Abrir assistente do fluxograma"
        aria-label="Abrir assistente do fluxograma"
      >
        <SparklesIcon className="h-6 w-6" />
      </button>
    )
  }

  return (
    <div
      ref={panelRef}
      className="flow-ai-panel"
      role="dialog"
      aria-modal="false"
      aria-label={title}
    >
      <header className="flow-ai-header">
        <div className="flow-ai-header-info">
          <span className="flow-ai-avatar" aria-hidden>
            <SparklesIcon className="h-5 w-5" />
          </span>
          <div>
            <strong>{title}</strong>
            <span className="flow-ai-status">
              {sending ? 'Pensando…' : 'online'}
            </span>
          </div>
        </div>
        <button
          type="button"
          className="flow-ai-close"
          onClick={onClose}
          title="Fechar chat"
          aria-label="Fechar chat"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </header>

      <div ref={listRef} className="flow-ai-messages">
        {messages.length === 0 && !sending && (
          <p className="flow-ai-empty">
            Peça para criar ou alterar o fluxograma. Ex.: “Crie o fluxo de
            abertura de ticket” ou “Adicione uma decisão após o processo
            Inicial e conecte Sim/Não”.
          </p>
        )}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flow-ai-bubble-row ${message.role === 'user' ? 'is-user' : 'is-assistant'}`}
          >
            {message.role === 'assistant' && (
              <span className="flow-ai-mini-avatar" aria-hidden>
                <SparklesIcon className="h-4 w-4" />
              </span>
            )}
            <div className={`flow-ai-bubble ${message.role}`}>
              <p>{message.content}</p>
              <time dateTime={message.created_at}>
                {new Date(message.created_at).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </time>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flow-ai-bubble-row is-assistant">
            <span className="flow-ai-mini-avatar" aria-hidden>
              <SparklesIcon className="h-4 w-4" />
            </span>
            <div className="flow-ai-bubble assistant flow-ai-typing" aria-live="polite">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>

      {error && <div className="flow-ai-error">{error}</div>}

      <footer className="flow-ai-composer">
        <textarea
          ref={inputRef}
          rows={2}
          value={input}
          disabled={!canSend || sending}
          placeholder="Digite sua mensagem aqui"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void handleSubmit()
            }
          }}
        />
        <button
          type="button"
          className="flow-ai-send"
          disabled={!input.trim() || sending || !canSend}
          onClick={() => void handleSubmit()}
        >
          Enviar
        </button>
      </footer>
    </div>
  )
}
